import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  GoodUnits,
  useUpdateGoodLoadedQuantityMutation,
  useUpdateGoodDeliveredQuantityMutation,
  ReasonCodeType,
  useGetTripQuery,
  useGetShipmentQuery,
  useGetOrganizationSettingsQuery,
} from "../../../graphql/generated";
import goodUnitLabel from "../../../utils/labels/goodUnitsLabel";
import ReasonCodeModal from "../../common/ReasonCodeModal/ReasonCodeModal";
import { queryClient } from "../../../providers/QueryClient";
import NumberTextField from "../../common/NumberTextField";
import { testIds } from "./LoadedQuantityModal.testIds";

interface LoadedQuantityModalProps {
  open: boolean;
  onClose: () => void;
  shipmentId: string;
  tripId: string;
  shipmentLocationId: string;
  good: {
    _id: string;
    label: string;
    quantity: number;
    loadedQuantity?: number | null;
    netLoadedQuantity?: number | null;
    deliveredQuantity?: number | null;
    netDeliveredQuantity?: number | null;
    unit?: GoodUnits | null;
    storageFacilityId?: string | null;
  };
  isLoaded?: boolean;
}

export const LoadedQuantityModal = ({
  open,
  onClose,
  shipmentId,
  tripId,
  shipmentLocationId,
  good,
  isLoaded = true,
}: LoadedQuantityModalProps) => {
  const { t } = useTranslation(["common", "orders"]);
  const [quantity, setQuantity] = useState<number>(
    isLoaded ? good.loadedQuantity || good.quantity : good.quantity
  );
  const [showReasonCode, setShowReasonCode] = useState(false);
  const [pendingQuantity, setPendingQuantity] = useState<number | null>(null);

  const orgSettings = useGetOrganizationSettingsQuery();
  const netEnabled =
    !!orgSettings.data?.organizationSettings?.dispatching
      ?.trackNetAndGrossVolumes;
  const [netQuantity, setNetQuantity] = useState<number | undefined>(
    isLoaded
      ? good.netLoadedQuantity ?? undefined
      : good.netDeliveredQuantity ?? undefined
  );

  const updateLoadedQuantityMutation = useUpdateGoodLoadedQuantityMutation();
  const updateDeliveredQuantityMutation =
    useUpdateGoodDeliveredQuantityMutation();

  const handleSubmit = async () => {
    const expectedQuantity = good.quantity;
    const difference = Math.abs(quantity - expectedQuantity);
    const percentageDifference = (difference / expectedQuantity) * 100;

    if (percentageDifference > 5) {
      setPendingQuantity(quantity);
      setShowReasonCode(true);
    } else {
      await updateQuantity(quantity);
    }
  };

  const updateQuantity = async (qty: number) => {
    try {
      if (isLoaded) {
        await updateLoadedQuantityMutation.mutateAsync({
          input: {
            shipmentId,
            goodId: good._id,
            loadedQuantity: qty,
            netLoadedQuantity: netEnabled ? netQuantity : undefined,
            shipmentLocationId,
          },
        });
      } else {
        // Build the input object conditionally
        const deliveredInput: {
          shipmentId: string;
          goodId: string;
          deliveredQuantity: number;
          netDeliveredQuantity?: number;
          storageFacilityId?: string;
          shipmentLocationId: string;
        } = {
          shipmentId,
          goodId: good._id,
          deliveredQuantity: qty,
          netDeliveredQuantity: netEnabled ? netQuantity : undefined,
          shipmentLocationId,
        };

        // Include storageFacilityId if it exists on the good
        if (good.storageFacilityId) {
          deliveredInput.storageFacilityId = good.storageFacilityId;
        }

        await updateDeliveredQuantityMutation.mutateAsync({
          input: deliveredInput,
        });
      }
      onClose();
      queryClient.refetchQueries(
        useGetTripQuery.getKey({
          id: tripId,
        })
      );
      queryClient.refetchQueries(
        useGetShipmentQuery.getKey({
          id: shipmentId,
        })
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  };

  const handleReasonCodeConfirm = async () => {
    if (pendingQuantity !== null) {
      await updateQuantity(pendingQuantity);
    }
  };

  const actionText: string = isLoaded
    ? t("orders:loadedQuantity", "Loaded Quantity")
    : t("orders:deliveredQuantity", "Delivered Quantity");

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionText} - {good.label}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Typography variant="body2">
              {t("orders:orderedQuantity", "Ordered Quantity")}: {good.quantity}{" "}
              {goodUnitLabel(good.unit || GoodUnits.Item)}
            </Typography>

            {!isLoaded && good.storageFacilityId && (
              <Typography variant="body2" color="text.secondary">
                {t("common:storageFacility", "Tank")}: {good.storageFacilityId}
              </Typography>
            )}

            <NumberTextField
              label={netEnabled ? t("common:gross", "Gross") : actionText}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              fullWidth
              inputProps={{
                "data-testid": testIds.loadedQuantityModalQuantity,
              } as React.InputHTMLAttributes<HTMLInputElement>}
              InputProps={{
                endAdornment: (
                  <Typography>
                    {goodUnitLabel(good.unit || GoodUnits.Item)}
                  </Typography>
                ),
              }}
            />

            {netEnabled && (
              <NumberTextField
                label={t("common:net", "Net")}
                value={netQuantity ?? ""}
                onChange={(e) =>
                  setNetQuantity(
                    e.target.value === ""
                      ? undefined
                      : Number(e.target.value)
                  )
                }
                fullWidth
                inputProps={{
                  "data-testid": testIds.loadedQuantityModalNetQuantity,
                } as React.InputHTMLAttributes<HTMLInputElement>}
                InputProps={{
                  endAdornment: (
                    <Typography>
                      {goodUnitLabel(good.unit || GoodUnits.Item)}
                    </Typography>
                  ),
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-testid={testIds.loadedQuantityModalCancel} onClick={onClose}>{t("common:cancel", "Cancel")}</Button>
          <LoadingButton data-testid={testIds.loadedQuantityModalSave}
            variant="contained"
            onClick={handleSubmit}
            loading={
              updateLoadedQuantityMutation.isLoading ||
              updateDeliveredQuantityMutation.isLoading
            }
          >
            {t("common:save", "Save")}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <ReasonCodeModal
        open={showReasonCode}
        onClose={() => {
          setShowReasonCode(false);
          setPendingQuantity(null);
        }}
        onConfirm={handleReasonCodeConfirm}
        title="Quantity Mismatch"
        description="The quantity differs by more than 5% from the expected quantity. Please provide a reason for this difference."
        shipmentId={shipmentId}
        locationId={shipmentLocationId}
        type={
          isLoaded
            ? ReasonCodeType.LoadedQuantityMismatch
            : ReasonCodeType.DeliveredQuantityMismatch
        }
      />
    </>
  );
};

export default LoadedQuantityModal;
